import type { AxiosResponse } from 'axios';

export const useApiInterceptor = () => {
  const { apiInstance } = useShopwareContext();

  /**
   * Add a response interceptor that sets a cookie based on the presence of a customer
   * in the API response. The cookie will be set when the response has `apiAlias`
   * equal to `"sales_channel_context"` or `"customer"`.
   *
   * - If `apiAlias` is `"sales_channel_context"`, the customer is taken from `response.data.customer`.
   * - If `apiAlias` is `"customer"`, the entire response data is treated as the customer.
   *
   * The cookie value is set to `"1"` if a customer exists, or `"0"` otherwise.
   *
   * @param cookieName - Name of the cookie to set
   * @param cookieOptions - Cookie options (maxAge, path, etc.)
   * @returns Interceptor ID for later removal
   */
  const addInterceptor = (
    cookieName: string,
    cookieOptions?: {
      maxAge?: number;
      path?: string;
      secure?: boolean;
      httpOnly?: boolean;
      sameSite?: 'strict' | 'lax' | 'none';
    },
  ): number => {
    return apiInstance._axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const { apiAlias } = response.data ?? {};

        if (apiAlias === 'sales_channel_context' || apiAlias === 'customer') {
          const cookie = useCookie(cookieName, {
            maxAge: cookieOptions?.maxAge ?? 60 * 60 * 24 * 7, // 7 days
            path: cookieOptions?.path ?? '/',
            secure: cookieOptions?.secure ?? !process.dev,
            httpOnly: cookieOptions?.httpOnly ?? false,
            sameSite: cookieOptions?.sameSite ?? 'lax',
          });

          let customer = null;

          if (apiAlias === 'sales_channel_context') {
            customer = response.data?.customer;
          }

          if (apiAlias === 'customer') {
            customer = response.data;
          }

          cookie.value = String(Number(Boolean(customer)));
        }

        return response;
      },
      (error) => Promise.reject(error),
    );
  };

  /**
   * Remove an interceptor
   * @param id - Interceptor ID
   */
  const removeInterceptor = (id: number): void => {
    apiInstance._axiosInstance.interceptors.response.eject(id);
  };

  return {
    addInterceptor,
    removeInterceptor,
  };
};
